import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';

/**
 * Export Utilities for Reports
 * Supports CSV, PDF, and Excel exports for various data types
 */

// ==================== Interfaces ====================

export interface ExportData {
  reportName: string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  metrics?: Array<{
    label: string;
    value: string | number;
  }>;
  tables?: Array<{
    title: string;
    headers: string[];
    rows: (string | number)[][];
  }>;
  charts?: Array<{
    title: string;
    data: Array<{ label: string; value: number }>;
  }>;
  additionalInfo?: string;
}

// ==================== CSV Export ====================

/**
 * Export data to CSV format
 */
export const exportToCSV = (data: ExportData): void => {
  try {
    let csvContent = '';

    // Add report header
    csvContent += `Report: ${data.reportName}\n`;
    if (data.dateRange) {
      csvContent += `Date Range: ${data.dateRange.startDate} to ${data.dateRange.endDate}\n`;
    }
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

    // Add metrics section
    if (data.metrics && data.metrics.length > 0) {
      csvContent += 'KEY METRICS\n';
      data.metrics.forEach(metric => {
        csvContent += `${metric.label},${metric.value}\n`;
      });
      csvContent += '\n';
    }

    // Add tables
    if (data.tables && data.tables.length > 0) {
      data.tables.forEach(table => {
        csvContent += `${table.title}\n`;

        // Headers
        const headersCsv = Papa.unparse([table.headers]);
        csvContent += headersCsv + '\n';

        // Rows
        const rowsCsv = Papa.unparse(table.rows);
        csvContent += rowsCsv + '\n\n';
      });
    }

    // Add chart data
    if (data.charts && data.charts.length > 0) {
      data.charts.forEach(chart => {
        csvContent += `${chart.title}\n`;
        csvContent += 'Label,Value\n';
        chart.data.forEach(item => {
          csvContent += `${item.label},${item.value}\n`;
        });
        csvContent += '\n';
      });
    }

    // Add additional info
    if (data.additionalInfo) {
      csvContent += `\nNotes:\n${data.additionalInfo}\n`;
    }

    // Download
    downloadFile(csvContent, `${sanitizeFilename(data.reportName)}_${getDateString()}.csv`, 'text/csv');
  } catch (error) {
    console.error('CSV export error:', error);
    throw new Error('Failed to export CSV');
  }
};

// ==================== PDF Export ====================

/**
 * Export data to PDF format
 */
export const exportToPDF = (data: ExportData): void => {
  try {
    const doc = new jsPDF();
    let yPosition = 20;

    // Add title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(data.reportName, 14, yPosition);
    yPosition += 10;

    // Add date range
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (data.dateRange) {
      doc.text(`Date Range: ${data.dateRange.startDate} to ${data.dateRange.endDate}`, 14, yPosition);
      yPosition += 6;
    }
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition);
    yPosition += 10;

    // Add metrics section
    if (data.metrics && data.metrics.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Key Metrics', 14, yPosition);
      yPosition += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      const metricsPerRow = 2;
      const metricWidth = 90;

      data.metrics.forEach((metric, index) => {
        const col = index % metricsPerRow;
        const xPos = 14 + (col * metricWidth);

        if (col === 0 && index > 0) {
          yPosition += 6;
        }

        doc.setFont('helvetica', 'bold');
        doc.text(`${metric.label}:`, xPos, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(String(metric.value), xPos + 50, yPosition);

        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
      });

      yPosition += 12;
    }

    // Add tables
    if (data.tables && data.tables.length > 0) {
      data.tables.forEach(table => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(table.title, 14, yPosition);
        yPosition += 6;

        autoTable(doc, {
          startY: yPosition,
          head: [table.headers],
          body: table.rows,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [41, 128, 185], fontStyle: 'bold' },
          margin: { left: 14, right: 14 },
          didDrawPage: (data) => {
            yPosition = data.cursor?.y || yPosition;
          }
        });

        yPosition += 10;
      });
    }

    // Add chart data
    if (data.charts && data.charts.length > 0) {
      data.charts.forEach(chart => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(chart.title, 14, yPosition);
        yPosition += 6;

        const chartData = chart.data.map(item => [item.label, item.value]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Label', 'Value']],
          body: chartData,
          theme: 'striped',
          styles: { fontSize: 9 },
          headStyles: { fillColor: [52, 152, 219] },
          margin: { left: 14, right: 14 },
          didDrawPage: (data) => {
            yPosition = data.cursor?.y || yPosition;
          }
        });

        yPosition += 10;
      });
    }

    // Add additional info
    if (data.additionalInfo) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      const splitText = doc.splitTextToSize(data.additionalInfo, 180);
      doc.text(splitText, 14, yPosition);
    }

    // Save PDF
    doc.save(`${sanitizeFilename(data.reportName)}_${getDateString()}.pdf`);
  } catch (error) {
    console.error('PDF export error:', error);
    throw new Error('Failed to export PDF');
  }
};

// ==================== Excel Export ====================

/**
 * Export data to Excel format
 */
export const exportToExcel = (data: ExportData): void => {
  try {
    const workbook = XLSX.utils.book_new();

    // Create summary sheet
    const summaryData: any[][] = [
      ['Report', data.reportName],
      ['Generated', new Date().toLocaleString()],
    ];

    if (data.dateRange) {
      summaryData.push(['Date Range', `${data.dateRange.startDate} to ${data.dateRange.endDate}`]);
    }

    summaryData.push([]); // Empty row

    // Add metrics to summary
    if (data.metrics && data.metrics.length > 0) {
      summaryData.push(['KEY METRICS']);
      data.metrics.forEach(metric => {
        summaryData.push([metric.label, metric.value]);
      });
    }

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

    // Set column widths
    summarySheet['!cols'] = [
      { wch: 30 },
      { wch: 30 }
    ];

    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Add each table as a separate sheet
    if (data.tables && data.tables.length > 0) {
      data.tables.forEach((table, index) => {
        const tableData = [table.headers, ...table.rows];
        const tableSheet = XLSX.utils.aoa_to_sheet(tableData);

        // Set column widths
        const colWidths = table.headers.map(() => ({ wch: 15 }));
        tableSheet['!cols'] = colWidths;

        // Sanitize sheet name (max 31 chars, no special chars)
        let sheetName = sanitizeSheetName(table.title);
        if (sheetName.length > 31) {
          sheetName = sheetName.substring(0, 28) + '...';
        }

        XLSX.utils.book_append_sheet(workbook, tableSheet, sheetName);
      });
    }

    // Add chart data as separate sheets
    if (data.charts && data.charts.length > 0) {
      data.charts.forEach(chart => {
        const chartData = [
          ['Label', 'Value'],
          ...chart.data.map(item => [item.label, item.value])
        ];
        const chartSheet = XLSX.utils.aoa_to_sheet(chartData);

        chartSheet['!cols'] = [
          { wch: 20 },
          { wch: 15 }
        ];

        let sheetName = sanitizeSheetName(chart.title);
        if (sheetName.length > 31) {
          sheetName = sheetName.substring(0, 28) + '...';
        }

        XLSX.utils.book_append_sheet(workbook, chartSheet, sheetName);
      });
    }

    // Save Excel file
    XLSX.writeFile(workbook, `${sanitizeFilename(data.reportName)}_${getDateString()}.xlsx`);
  } catch (error) {
    console.error('Excel export error:', error);
    throw new Error('Failed to export Excel');
  }
};

// ==================== Helper Functions ====================

/**
 * Download a file with given content
 */
const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Sanitize filename by removing special characters
 */
const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^a-z0-9_\-]/gi, '_')
    .replace(/_+/g, '_')
    .toLowerCase();
};

/**
 * Sanitize sheet name for Excel (no special characters)
 */
const sanitizeSheetName = (name: string): string => {
  return name
    .replace(/[\\\/\?\*\[\]]/g, '')
    .replace(/:/g, '-')
    .trim();
};

/**
 * Get formatted date string for filenames
 */
const getDateString = (): string => {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
};

// ==================== Quick Export Functions ====================

/**
 * Quick export for simple table data
 */
export const exportTableToCSV = (
  reportName: string,
  headers: string[],
  rows: (string | number)[][],
  dateRange?: { startDate: string; endDate: string }
): void => {
  exportToCSV({
    reportName,
    dateRange,
    tables: [{ title: reportName, headers, rows }]
  });
};

/**
 * Quick export for chart data
 */
export const exportChartToCSV = (
  reportName: string,
  chartTitle: string,
  data: Array<{ label: string; value: number }>,
  dateRange?: { startDate: string; endDate: string }
): void => {
  exportToCSV({
    reportName,
    dateRange,
    charts: [{ title: chartTitle, data }]
  });
};

/**
 * Export report as PDF with visual screenshot
 */
export const exportToPDFWithScreenshot = async (
  reportName: string,
  element: HTMLElement,
  dateRange?: { startDate: string; endDate: string }
): Promise<void> => {
  try {
    // Show loading indicator (optional)
    console.log('Capturing report screenshot...');

    // Capture the element as canvas
    const canvas = await html2canvas(element, {
      scale: 2, // Higher quality
      useCORS: true, // Handle cross-origin images
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    });

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Calculate dimensions to fit on page
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Reserve space for header (title + date) and footer
    const headerHeight = 25;
    const footerHeight = 10;
    const availableHeight = pdfHeight - headerHeight - footerHeight;
    const availableWidth = pdfWidth - 20; // 10mm margin on each side

    // Add title and date range
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(reportName, pdfWidth / 2, 10, { align: 'center' });

    if (dateRange) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Date Range: ${dateRange.startDate} to ${dateRange.endDate}`,
        pdfWidth / 2,
        17,
        { align: 'center' }
      );
    }

    // Calculate scaling to fit the image in available space
    const imgWidthMM = canvas.width * 0.264583; // Convert pixels to mm (96 DPI)
    const imgHeightMM = canvas.height * 0.264583;

    // Scale to fit within available space while maintaining aspect ratio
    const scaleToFitWidth = availableWidth / imgWidthMM;
    const scaleToFitHeight = availableHeight / imgHeightMM;
    const scale = Math.min(scaleToFitWidth, scaleToFitHeight, 1); // Don't scale up, max 1x

    const finalWidth = imgWidthMM * scale;
    const finalHeight = imgHeightMM * scale;

    // Center the image horizontally
    const imgX = (pdfWidth - finalWidth) / 2;
    const imgY = headerHeight;

    console.log(`PDF: Canvas ${canvas.width}x${canvas.height}px, Scaled to ${finalWidth.toFixed(1)}x${finalHeight.toFixed(1)}mm`);

    // Check if image fits on one page
    if (finalHeight <= availableHeight) {
      // Fits on one page
      pdf.addImage(imgData, 'PNG', imgX, imgY, finalWidth, finalHeight);
      console.log('PDF: Fits on single page');
    } else {
      // Need multiple pages - split the image
      console.log('PDF: Requires multiple pages');

      let sourceY = 0; // Position in the source image (pixels)
      let pageNum = 0;

      while (sourceY < canvas.height) {
        if (pageNum > 0) {
          pdf.addPage();
        }

        // Calculate how much of the image fits on this page (in pixels)
        const remainingHeight = canvas.height - sourceY;
        const pageHeightPixels = Math.min(
          remainingHeight,
          availableHeight / 0.264583 / scale
        );

        // Create a temporary canvas for this page's content
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = pageHeightPixels;
        const tempCtx = tempCanvas.getContext('2d');

        if (tempCtx) {
          // Copy the relevant portion of the original canvas
          tempCtx.drawImage(
            canvas,
            0, sourceY, canvas.width, pageHeightPixels,
            0, 0, canvas.width, pageHeightPixels
          );

          const pageImgData = tempCanvas.toDataURL('image/png');
          const pageHeightMM = pageHeightPixels * 0.264583 * scale;

          pdf.addImage(pageImgData, 'PNG', imgX, imgY, finalWidth, pageHeightMM);
          console.log(`PDF: Page ${pageNum + 1}, sourceY: ${sourceY}, height: ${pageHeightPixels}px`);
        }

        sourceY += pageHeightPixels;
        pageNum++;

        // Safety check to prevent infinite loops
        if (pageNum > 20) {
          console.warn('PDF: Exceeded maximum page limit (20), truncating');
          break;
        }
      }
    }

    // Add footer
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.text(
        `Generated: ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
        pdfWidth / 2,
        pdfHeight - 5,
        { align: 'center' }
      );
    }

    // Save
    pdf.save(`${sanitizeFilename(reportName)}_${getDateString()}.pdf`);
    console.log('PDF exported successfully');
  } catch (error) {
    console.error('PDF screenshot export error:', error);
    throw new Error('Failed to export PDF with screenshot');
  }
};

/**
 * Extract table data from DOM element (for existing components)
 */
export const extractTableDataFromDOM = (tableElement: HTMLTableElement): {
  headers: string[];
  rows: string[][];
} => {
  const headers: string[] = [];
  const rows: string[][] = [];

  // Extract headers
  const headerCells = tableElement.querySelectorAll('thead th');
  headerCells.forEach(cell => {
    headers.push(cell.textContent?.trim() || '');
  });

  // Extract rows
  const bodyRows = tableElement.querySelectorAll('tbody tr');
  bodyRows.forEach(row => {
    const rowData: string[] = [];
    const cells = row.querySelectorAll('td');
    cells.forEach(cell => {
      rowData.push(cell.textContent?.trim() || '');
    });
    if (rowData.length > 0) {
      rows.push(rowData);
    }
  });

  return { headers, rows };
};
